import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// ProjectsTable — flat table view of Projects & Meetings. Each project
// renders as a group header row with its tasks listed underneath; the
// caller's one-off tasks pin to the bottom in their own group. Backed
// by GET /users/me/project-table which delivers everything in one
// round-trip (readiness, blocker detail, dependent counts, flow
// positions, team rollup).
//
// Side effects worth knowing:
//  • Clicking the project name calls onSelectProject(id) — the parent
//    opens the existing project detail pane.
//  • Clicking the chevron toggles which projects show their task rows.
//  • Filter pills (All / Mine / Unassigned) live on the TASK level and
//    cascade to hide projects with no matching tasks.
//  • Inline "+ Add task" POSTs to /tasks with optional predecessor ids.
//  • A waiting row's Nudge button reuses the same composer as the
//    scheduler panel.
import { notify } from "./Toast";
import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../api/client";
import { useAuth } from "../hooks/useAuth";
import NudgeComposer from "./NudgeComposer";
import TaskCompletionModal from "./TaskCompletionModal";
// Shared inline style for the "Free to start" popover items. Kept as
// inline-style instead of a CSS class so the menu doesn't depend on a
// separate styles.css change going out at the same time.
const menuItemStyle = {
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: "8px 10px",
    background: "transparent",
    border: 0,
    color: "var(--text, #e8eaf0)",
    font: "inherit",
    fontWeight: 600,
    fontSize: 13,
    cursor: "pointer",
    borderRadius: 6,
};
// True when a project's due date is already past OR within the next 7
// days. Date-only (YYYY-MM-DD) string compare against a 7-day horizon —
// no lower bound, so overdue projects always qualify. Mirrors the
// sidebar's "Due ≤ 7d" project filter so both views agree.
function isDueSoonOrOverdue(due) {
    if (!due)
        return false;
    const horizon = new Date();
    horizon.setDate(horizon.getDate() + 7);
    const horizonIso = horizon.toISOString().slice(0, 10);
    return due <= horizonIso;
}
// Same window but for a one-time event's meeting instant (ISO datetime):
// the meeting is at-or-before the 7-day horizon (overdue meetings in the
// past always qualify). Used to fold one-time meetings into "Due ≤ 7d".
function isEventDueSoonOrOverdue(eventAt) {
    if (!eventAt)
        return false;
    const t = new Date(eventAt).getTime();
    if (Number.isNaN(t))
        return false;
    return t <= Date.now() + 7 * 24 * 60 * 60 * 1000;
}
// Map a task's raw status + readiness to a display pill. Free to start
// is the new "not_started AND no incomplete predecessors" label — the
// previous "Not started" state lumped together blocked + ready-to-go
// work, which read the same to the user.
function statusOf(task) {
    if (task.status === "completed")
        return { label: "Complete", cls: "ok" };
    if (task.readiness === "waiting")
        return { label: "Waiting", cls: "warn" };
    if (task.status === "blocked")
        return { label: "Blocked", cls: "danger" };
    if (task.status === "in_progress")
        return { label: "In progress", cls: "info" };
    if (task.status === "not_started")
        return { label: "Free to start", cls: "free" };
    return { label: task.status, cls: "" };
}
// Project-level rollup pill — derived from the constituent tasks. A
// project with any waiting task reads "At risk", but ONLY when the
// project has a due_date set — without a deadline there's no time
// pressure, so a "waiting" task is just normal sequencing, not a risk.
// All-complete reads "Complete"; anything in flight reads "On track".
function projectStatusOf(project) {
    if (project.status === "completed")
        return { label: "Complete", cls: "ok" };
    if (project.status === "archived")
        return { label: "Archived", cls: "" };
    const tasks = project.tasks;
    if (tasks.length === 0)
        return { label: "Empty", cls: "" };
    if (tasks.every((t) => t.status === "completed"))
        return { label: "Complete", cls: "ok" };
    // At risk only fires when a real deadline is on the line. Without
    // project.due_date the "any task waiting" signal isn't urgent — it's
    // just sequencing — so fall through to the normal active labels.
    if (project.due_date != null &&
        tasks.some((t) => t.readiness === "waiting"))
        return { label: "At risk", cls: "warn" };
    if (tasks.some((t) => t.status === "in_progress"))
        return { label: "On track", cls: "ok" };
    return { label: "Not started", cls: "" };
}
function fmtDue(iso) {
    if (!iso)
        return "—";
    const [y, m, d] = iso.split("-").map(Number);
    const date = new Date(y, (m || 1) - 1, d || 1);
    return date.toLocaleDateString(undefined, {
        weekday: "short",
        day: "numeric",
    });
}
// Has at least one blocker we could actually nudge (an assignee on the
// predecessor task). Empty blocker assignees = no one to poke.
function hasNudgeTarget(blockers) {
    return blockers.some((b) => b.assignees.length > 0);
}
export default function ProjectsTable({ onSelectProject, refreshSignal = 0, onFilterChange, initialFilter, }) {
    const { user: currentUser } = useAuth();
    const [feed, setFeed] = useState(null);
    const [loading, setLoading] = useState(false);
    const [taskFilter, setTaskFilter] = useState(initialFilter === "mine" ||
        initialFilter === "unassigned" ||
        initialFilter === "due7"
        ? initialFilter
        : "all");
    // Project ids whose task rows are HIDDEN. Start empty so everything's
    // expanded by default — the user can collapse what they don't want.
    const [collapsed, setCollapsed] = useState(new Set());
    // Inline "+ Add task" state. Only one row open at a time.
    const [addingFor, setAddingFor] = useState(null);
    const [nudgeBlockers, setNudgeBlockers] = useState(null);
    // Double-click → edit. Holds the task currently being edited in the
    // modal. canManage check happens before opening (workers see the
    // modal in read-only / disabled form — same posture as the rest of
    // the table edits).
    const [editingTask, setEditingTask] = useState(null);
    // When a task is marked complete from the edit modal, we route through
    // the completion flow (BU/Activity gate + missing-teammate check + time
    // logging) instead of a bare status PATCH.
    const [completingTask, setCompletingTask] = useState(null);
    // Surface fetch errors instead of swallowing them — the original
    // `catch {}` left the table looking permanently empty when the feed
    // endpoint 500'd, with no indication to the user that anything was
    // wrong. Render the message in the empty slot so people can act on it.
    const [error, setError] = useState(null);
    // Team-member workload filter (admin / manager / owner only). When set
    // to a user id, the table narrows to tasks assigned to that person across
    // every project so a lead can eyeball one teammate's load and follow up on
    // what needs to go out. "" = everyone (no member filter).
    const [members, setMembers] = useState([]);
    const [memberFilter, setMemberFilter] = useState("");
    const canManage = currentUser?.role === "admin" || currentUser?.role === "manager";
    // Owners join managers/admins for the workload view — they manage their
    // own BU's people and need the same "who's loaded with what" read.
    const canViewWorkload = currentUser?.role === "admin" ||
        currentUser?.role === "manager" ||
        currentUser?.role === "owner";
    async function load() {
        setLoading(true);
        setError(null);
        try {
            const data = await api.get("/users/me/project-table");
            setFeed(data);
        }
        catch (e) {
            const msg = e instanceof ApiError
                ? `${e.status} — ${e.message}`
                : e instanceof Error
                    ? e.message
                    : "Couldn't load the table.";
            setError(msg);
        }
        finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        void load();
        const onFocus = () => void load();
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
        // refreshSignal is bumped by the parent on create/delete so the
        // table refetches its feed — without this the table view rendered
        // stale data while the sidebar updated optimistically.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshSignal]);
    // Keep the parent in sync with the active filter so it can hide its
    // one-off / one-time widgets while "Due ≤ 7d" is showing the filtered
    // versions inline.
    useEffect(() => {
        onFilterChange?.(taskFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taskFilter]);
    // Load the roster for the workload dropdown. /coworkers already returns
    // EVERYONE for admins and a BU-shared subset for managers/owners, so it
    // maps exactly to "team members within their respective BU" — no extra
    // gating needed here. Workers never reach this (canViewWorkload is false).
    useEffect(() => {
        if (!canViewWorkload)
            return;
        let cancelled = false;
        (async () => {
            try {
                const list = await api.get("/users/coworkers?include_self=true");
                if (!cancelled)
                    setMembers(list);
            }
            catch {
                /* leave empty — the dropdown just won't render */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [canViewWorkload]);
    function toggleCollapsed(id) {
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(id))
                next.delete(id);
            else
                next.add(id);
            return next;
        });
    }
    // The pill filter (All / Mine / Unassigned / Due ≤ 7d) — applied per
    // task row. "Due ≤ 7d" is task-level: keep tasks overdue or due within
    // the next 7 days. (One-off tasks + one-time meetings fold in separately,
    // below the project rows.)
    function pillMatches(t) {
        if (taskFilter === "all")
            return true;
        if (taskFilter === "due7")
            return isDueSoonOrOverdue(t.due_date);
        if (taskFilter === "mine") {
            if (!currentUser)
                return false;
            return t.assignees.some((a) => a.id === currentUser.id);
        }
        if (taskFilter === "unassigned")
            return t.assignees.length === 0;
        return true;
    }
    // The workload filter — independent axis that ANDs with the pill. When a
    // member is picked, keep only tasks assigned to them.
    function memberMatches(t) {
        if (!memberFilter)
            return true;
        return t.assignees.some((a) => a.id === memberFilter);
    }
    // Pill click handler. Mine / Unassigned are assignee-based and would
    // contradict an active member filter (Mine + someone-else = always empty;
    // Unassigned requires zero assignees), so picking either clears the member
    // filter. All / Due ≤ 7d compose with it cleanly and leave it intact.
    function selectPill(f) {
        setTaskFilter(f);
        if (f === "mine" || f === "unassigned")
            setMemberFilter("");
    }
    const projects = useMemo(() => {
        if (!feed)
            return [];
        if (taskFilter === "all" && !memberFilter)
            return feed.projects;
        return feed.projects
            .map((p) => ({
            ...p,
            tasks: p.tasks.filter((t) => pillMatches(t) && memberMatches(t)),
        }))
            .filter((p) => p.tasks.length > 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [feed, taskFilter, memberFilter, currentUser?.id]);
    // Open (non-completed) task count per assignee, for the workload dropdown
    // labels — a lead sees who's carrying how much before they even filter.
    const memberTaskCounts = useMemo(() => {
        const counts = new Map();
        if (!feed)
            return counts;
        for (const p of feed.projects) {
            for (const t of p.tasks) {
                if (t.status === "completed")
                    continue;
                for (const a of t.assignees) {
                    counts.set(a.id, (counts.get(a.id) ?? 0) + 1);
                }
            }
        }
        return counts;
    }, [feed]);
    const selectedMemberName = memberFilter
        ? members.find((m) => m.id === memberFilter)?.name ?? "that teammate"
        : "";
    const mineCount = useMemo(() => {
        if (!feed || !currentUser)
            return 0;
        let n = 0;
        for (const p of feed.projects) {
            for (const t of p.tasks) {
                if (t.assignees.some((a) => a.id === currentUser.id))
                    n++;
            }
        }
        return n;
    }, [feed, currentUser]);
    const unassignedCount = useMemo(() => {
        if (!feed)
            return 0;
        let n = 0;
        for (const p of feed.projects) {
            for (const t of p.tasks)
                if (t.assignees.length === 0)
                    n++;
        }
        return n;
    }, [feed]);
    // One-off tasks that are overdue or due within 7 days (active only) —
    // folded into the "Due ≤ 7d" view below the project rows. One-off rows
    // don't carry assignee ids (only a creator/owner name), so a member
    // filter can't truthfully attribute them to one person — hide them while
    // a member is selected rather than show ones we can't match.
    const dueOneOffs = useMemo(() => {
        if (!feed || memberFilter)
            return [];
        return feed.one_off_tasks.filter((o) => !o.is_completed && isDueSoonOrOverdue(o.due_date ?? null));
    }, [feed, memberFilter]);
    // One-time meetings whose meeting instant is overdue or within 7 days
    // (active only). These carry attendee ids, so the member filter narrows
    // them to meetings the selected teammate is actually attending.
    const dueEvents = useMemo(() => {
        if (!feed)
            return [];
        let list = feed.one_time_events.filter((e) => !e.is_completed && isEventDueSoonOrOverdue(e.event_at));
        if (memberFilter)
            list = list.filter((e) => e.attendees.some((a) => a.id === memberFilter));
        return list;
    }, [feed, memberFilter]);
    // Badge count for "Due ≤ 7d" — every matching item: project tasks +
    // one-off tasks + one-time meetings due/overdue within the window.
    const dueSoonCount = useMemo(() => {
        if (!feed)
            return 0;
        let n = 0;
        for (const p of feed.projects)
            for (const t of p.tasks)
                if (isDueSoonOrOverdue(t.due_date))
                    n++;
        return n + dueOneOffs.length + dueEvents.length;
    }, [feed, dueOneOffs, dueEvents]);
    return (_jsxs("div", { className: "pm-table-wrap", children: [_jsxs("div", { className: "pm-table-filters", children: [_jsx("span", { className: "pm-table-filters-label", children: "Show tasks" }), _jsx("button", { type: "button", className: `pm-table-filter ${taskFilter === "all" ? "active" : ""}`, onClick: () => selectPill("all"), children: "All" }), _jsxs("button", { type: "button", className: `pm-table-filter ${taskFilter === "mine" ? "active" : ""}`, onClick: () => selectPill("mine"), children: ["Mine", _jsx("span", { className: "pm-table-filter-count", children: mineCount })] }), _jsxs("button", { type: "button", className: `pm-table-filter ${taskFilter === "unassigned" ? "active" : ""}`, onClick: () => selectPill("unassigned"), children: ["Unassigned", _jsx("span", { className: "pm-table-filter-count", children: unassignedCount })] }), _jsxs("button", { type: "button", className: `pm-table-filter ${taskFilter === "due7" ? "active" : ""}`, onClick: () => selectPill("due7"), title: "Tasks, one-off tasks & meetings overdue or due within 7 days", children: ["Due \u2264 7d", _jsx("span", { className: "pm-table-filter-count", children: dueSoonCount })] }), canViewWorkload && members.length > 0 && (_jsxs("label", { className: "pm-table-workload", title: "Filter by who a task is assigned to", children: [_jsx("span", { className: "pm-table-workload-label", children: "Workload" }), _jsxs("select", { className: "pm-table-workload-select", value: memberFilter, onChange: (e) => {
                                    const v = e.target.value;
                                    setMemberFilter(v);
                                    // Mine / Unassigned would contradict a specific member — drop
                                    // back to All so the member's tasks actually show.
                                    if (v &&
                                        (taskFilter === "mine" || taskFilter === "unassigned"))
                                        setTaskFilter("all");
                                }, children: [_jsx("option", { value: "", children: "Everyone" }), members.map((m) => {
                                        const n = memberTaskCounts.get(m.id) ?? 0;
                                        return (_jsxs("option", { value: m.id, children: [m.name, m.id === currentUser?.id ? " (me)" : "", " (", n, ")"] }, m.id));
                                    })] }), memberFilter && (_jsx("button", { type: "button", className: "pm-table-workload-clear", onClick: () => setMemberFilter(""), title: "Clear member filter", "aria-label": "Clear member filter", children: "\u2715" }))] })), _jsx("span", { style: { flex: 1 } }), _jsx("button", { type: "button", className: "btn-link", onClick: () => void load(), disabled: loading, title: "Refresh", children: loading ? "Refreshing…" : "↻ Refresh" })] }), _jsxs("div", { className: "pm-table", children: [_jsxs("div", { className: "pm-table-head", children: [_jsx("span", { children: "Name" }), _jsx("span", { children: "Owner" }), _jsx("span", { children: "Status" }), _jsx("span", { children: "Due" }), _jsx("span", { style: { textAlign: "right" }, children: "Progress" })] }), loading && !feed && (_jsx("div", { className: "pm-table-empty muted small", children: "Loading the table\u2026" })), error && !loading && (_jsxs("div", { className: "pm-table-empty error", children: ["Couldn't load the table \u2014 ", error, ". Try", " ", _jsx("button", { type: "button", className: "btn-link", onClick: () => void load(), children: "refresh" }), "."] })), !loading &&
                        !error &&
                        feed &&
                        projects.length === 0 &&
                        !(taskFilter === "due7" &&
                            (dueOneOffs.length > 0 || dueEvents.length > 0)) && (_jsx("div", { className: "pm-table-empty muted small", children: memberFilter
                            ? taskFilter === "due7"
                                ? `Nothing assigned to ${selectedMemberName} is overdue or due in the next 7 days.`
                                : `No open tasks assigned to ${selectedMemberName}.`
                            : taskFilter === "mine"
                                ? "Nothing assigned to you. Try All or Unassigned."
                                : taskFilter === "unassigned"
                                    ? "No unassigned tasks — everyone's claimed."
                                    : taskFilter === "due7"
                                        ? "Nothing overdue or due in the next 7 days."
                                        : "No projects yet." })), projects.map((project) => {
                        const isCollapsed = collapsed.has(project.id);
                        const status = projectStatusOf(project);
                        return (_jsxs("div", { className: "pm-table-group", children: [_jsxs("div", { className: "pm-table-row pm-table-project", children: [_jsxs("span", { className: "pm-table-cell-name", children: [_jsx("button", { type: "button", className: "pm-table-chevron", onClick: () => toggleCollapsed(project.id), "aria-label": isCollapsed ? "Expand" : "Collapse", children: isCollapsed ? "▸" : "▾" }), _jsxs("button", { type: "button", className: "pm-table-project-name", onClick: () => onSelectProject(project.id), title: "Open project editor", children: [project.name, _jsx("span", { className: "pm-table-open-arrow", children: " \u2197" })] }), project.business_unit_name && (_jsx("span", { className: "pill pm-table-bu", children: project.business_unit_name })), _jsxs("span", { className: `pill pm-table-priority p${project.priority}`, title: "Project priority (1 = urgent)", children: ["P", project.priority] })] }), _jsx("span", { className: "pm-table-cell-owner", title: project.team_size === 0
                                                ? "No one is on this project yet"
                                                : project.assignee_names.length > 0
                                                    ? // Multi-line list so the names are readable
                                                        // when the count gets high. Native title
                                                        // tooltips render newlines.
                                                        project.assignee_names.join("\n")
                                                    : `${project.team_size} people`, children: project.team_size === 0
                                                ? "No one yet"
                                                : project.team_size === 1
                                                    ? project.assignee_names[0]
                                                    : `${project.team_size} people` }), _jsx("span", { className: "pm-table-cell-status", children: _jsx("span", { className: `pill ${status.cls}`, children: status.label }) }), _jsx("span", { className: "pm-table-cell-due", children: fmtDue(project.due_date) }), _jsxs("span", { className: "pm-table-cell-progress", children: [_jsx("span", { className: "pm-table-progress-bar", children: _jsx("span", { className: "pm-table-progress-fill", style: { width: `${project.progress_percent}%` } }) }), _jsxs("span", { className: "pm-table-progress-pct", children: [project.progress_percent, "%"] })] })] }), !isCollapsed && (_jsxs(_Fragment, { children: [project.tasks.map((task) => (_jsx(TaskRow, { task: task, onNudge: (blockers) => setNudgeBlockers(blockers), onClaim: async () => {
                                                try {
                                                    await api.post(`/tasks/${task.id}/claim`);
                                                    await load();
                                                }
                                                catch (e) {
                                                    notify.error(e instanceof Error
                                                        ? e.message
                                                        : "Couldn't claim that task.");
                                                }
                                            }, onEdit: canManage
                                                ? () => setEditingTask({ task, projectId: project.id })
                                                : undefined }, task.id))), addingFor === project.id ? (_jsx(AddTaskInline, { project: project, onCancel: () => setAddingFor(null), onSaved: async () => {
                                                setAddingFor(null);
                                                await load();
                                            } })) : (_jsxs("div", { className: "pm-table-row pm-table-add", onClick: () => setAddingFor(project.id), role: "button", tabIndex: 0, onKeyDown: (e) => {
                                                if (e.key === "Enter" || e.key === " ") {
                                                    e.preventDefault();
                                                    setAddingFor(project.id);
                                                }
                                            }, children: [_jsx("span", { className: "pm-table-cell-name pm-table-add-text", children: "+ Add task" }), _jsx("span", {}), _jsx("span", {}), _jsx("span", {}), _jsx("span", {})] }))] }))] }, project.id));
                    }), taskFilter === "due7" &&
                        (dueOneOffs.length > 0 || dueEvents.length > 0) && (_jsxs("div", { className: "pm-table-group", children: [_jsxs("div", { className: "pm-table-row pm-table-project", children: [_jsxs("span", { className: "pm-table-cell-name", children: [_jsx("span", { className: "pm-table-project-name", children: "One-off tasks & meetings" }), _jsx("span", { className: "pill pm-table-bu", children: dueOneOffs.length + dueEvents.length })] }), _jsx("span", {}), _jsx("span", {}), _jsx("span", {}), _jsx("span", {})] }), dueOneOffs.map((o) => (_jsxs("div", { className: "pm-table-row pm-table-task is-editable", onClick: () => onSelectProject(o.project_id), role: "button", tabIndex: 0, onKeyDown: (e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        onSelectProject(o.project_id);
                                    }
                                }, title: "Open this one-off task", children: [_jsxs("span", { className: "pm-table-cell-name pm-table-cell-task-name", children: [_jsx("span", { className: "pm-table-task-title", children: o.title }), _jsx("span", { className: "muted small", children: "One-off task" })] }), _jsx("span", { className: "pm-table-cell-owner", children: o.owner_name ?? "—" }), _jsx("span", { className: "pm-table-cell-status", children: _jsx("span", { className: `pill ${o.scheduled_at ? "info" : "warn"}`, children: o.scheduled_at ? "Scheduled" : "Needs scheduling" }) }), _jsx("span", { className: "pm-table-cell-due", children: fmtDue(o.due_date ?? null) }), _jsx("span", { className: "pm-table-cell-progress" })] }, o.task_id))), dueEvents.map((e) => (_jsxs("div", { className: "pm-table-row pm-table-task is-editable", onClick: () => onSelectProject(e.project_id), role: "button", tabIndex: 0, onKeyDown: (ev) => {
                                    if (ev.key === "Enter" || ev.key === " ") {
                                        ev.preventDefault();
                                        onSelectProject(e.project_id);
                                    }
                                }, title: "Open this meeting", children: [_jsxs("span", { className: "pm-table-cell-name pm-table-cell-task-name", children: [_jsx("span", { className: "pm-table-task-title", children: e.title }), _jsx("span", { className: "muted small", children: "Meeting" })] }), _jsx("span", { className: "pm-table-cell-owner", children: e.attendees.length === 1
                                            ? e.attendees[0].name
                                            : `${e.attendees.length} attending` }), _jsx("span", { className: "pm-table-cell-status", children: _jsx("span", { className: `pill ${e.event_at ? "info" : "warn"}`, children: e.event_at ? "Scheduled" : "No time" }) }), _jsx("span", { className: "pm-table-cell-due", children: e.event_at
                                            ? new Date(e.event_at).toLocaleDateString(undefined, {
                                                month: "short",
                                                day: "numeric",
                                            })
                                            : "—" }), _jsx("span", { className: "pm-table-cell-progress" })] }, e.task_id)))] }))] }), _jsx(NudgeComposer, { open: nudgeBlockers !== null, blockers: nudgeBlockers ?? [], onClose: () => setNudgeBlockers(null) }), editingTask && (_jsx(EditTaskModal, { task: editingTask.task, onClose: () => setEditingTask(null), onSaved: async () => {
                    setEditingTask(null);
                    await load();
                }, onDeleted: async () => {
                    setEditingTask(null);
                    await load();
                }, onRequestComplete: () => {
                    const ctx = editingTask;
                    setEditingTask(null);
                    setCompletingTask(ctx);
                } })), completingTask && (_jsx(TaskCompletionModal, { taskId: completingTask.task.id, projectId: completingTask.projectId, taskTitle: completingTask.task.title, onClose: () => setCompletingTask(null), onCompleted: async () => {
                    setCompletingTask(null);
                    await load();
                } }))] }));
}
function TaskRow({ task, onNudge, onClaim, onEdit, }) {
    const { user: currentUser } = useAuth();
    const [claiming, setClaiming] = useState(false);
    // "Free to start" popover state. When the user clicks the status
    // pill on a ready task, we open a tiny menu with:
    //   ▶ Start task → sub-menu: Start now (Study Clock) / Schedule
    //   ✓ Mark complete → defers to onEdit() so they go through the
    //     existing TaskCompletionModal (BU/Activity/coworker credits)
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuSub, setMenuSub] = useState("root");
    // Close the menu on any outside click. Listening at document
    // level so a click anywhere except the menu/pill dismisses it.
    useEffect(() => {
        if (!menuOpen)
            return;
        const onDoc = () => setMenuOpen(false);
        document.addEventListener("click", onDoc);
        return () => document.removeEventListener("click", onDoc);
    }, [menuOpen]);
    async function startNow() {
        setMenuOpen(false);
        try {
            await api.post("/me/study-clock/start", { task_id: task.id });
            window.dispatchEvent(new Event("study-clock:changed"));
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't start Study Clock");
        }
    }
    function scheduleLater() {
        setMenuOpen(false);
        // Hand the task id to Calendar via nav state — the existing
        // "Schedule button from Today" pattern (#198) reads it and opens
        // BlockEditor pre-filled. We use window.location.assign so this
        // works without needing useNavigate threaded into every caller.
        try {
            const url = `/calendar?taskId=${encodeURIComponent(task.id)}`;
            window.location.assign(url);
        }
        catch {
            /* no-op */
        }
    }
    const st = statusOf(task);
    const waiting = task.readiness === "waiting";
    const done = task.status === "completed";
    const unassigned = task.assignees.length === 0;
    const startable = st.cls === "free";
    // Owner column copy. When the row is unassigned and the caller isn't
    // already on it, the cell becomes a "Take it" CTA instead of dead
    // grey text.
    const alreadyMine = !!currentUser?.id
        && task.assignees.some((a) => a.id === currentUser.id);
    const ownerLabel = unassigned
        ? "Unassigned"
        : task.assignees.length === 1
            ? task.assignees[0].name
            : `${task.assignees[0].name} +${task.assignees.length - 1}`;
    // Tooltip shown on hover when the cell shows a collapsed "+N" — one
    // assignee per line so it stays readable when the list grows. Native
    // title attributes render \n as line breaks.
    const ownerTooltip = unassigned
        ? ""
        : task.assignees.length === 1
            ? task.assignees[0].name
            : task.assignees.map((a) => a.name).join("\n");
    return (_jsxs("div", { className: `pm-table-row pm-table-task ${waiting ? "is-waiting" : ""} ${done ? "is-done" : ""} ${onEdit ? "is-editable" : ""}`, onDoubleClick: onEdit, title: onEdit ? "Double-click to edit" : undefined, children: [_jsxs("span", { className: "pm-table-cell-name pm-table-cell-task-name", children: [_jsx("span", { className: "pm-table-task-title", children: task.title }), task.flow_position != null && task.flow_total != null && (_jsxs("span", { className: "muted small pm-table-flow", children: ["Task ", task.flow_position, " of ", task.flow_total] })), task.is_bottleneck && task.dependent_count > 0 && (_jsxs("span", { className: "pill pm-table-unblocks", title: `${task.dependent_count} downstream task${task.dependent_count === 1 ? "" : "s"} depend${task.dependent_count === 1 ? "s" : ""} on this one`, children: ["\u21B3 Unblocks ", task.dependent_count, " ", task.dependent_count === 1 ? "task" : "tasks"] })), waiting && task.blocked_by.length > 0 && (_jsxs("span", { className: "pm-table-blocked-line", children: ["\u21B3 Waiting on:", " ", _jsx("span", { className: "pm-table-blocked-name", children: task.blocked_by.join(", ") })] }))] }), _jsx("span", { className: `pm-table-cell-owner ${unassigned ? "muted" : ""}`, title: ownerTooltip || undefined, children: unassigned && !done && !alreadyMine ? (_jsx("button", { type: "button", className: "pm-table-claim", disabled: claiming, onClick: async (e) => {
                        e.stopPropagation();
                        setClaiming(true);
                        try {
                            await onClaim();
                        }
                        finally {
                            setClaiming(false);
                        }
                    }, title: "Assign this task to yourself", children: claiming ? "Taking…" : "Take it" })) : (ownerLabel) }), _jsxs("span", { className: "pm-table-cell-status", children: [startable ? (
                    // Clickable status pill opens a Start / Mark-complete menu.
                    // Wrapped in a positioned span so the menu can absolute-
                    // position to the pill. stopPropagation on the click so
                    // the row's double-click handler doesn't also fire.
                    _jsxs("span", { className: "pm-table-free-wrap", style: { position: "relative", display: "inline-block" }, onClick: (e) => e.stopPropagation(), children: [_jsxs("button", { type: "button", className: `pill ${st.cls} pm-table-free-pill`, onClick: (e) => {
                                    e.stopPropagation();
                                    setMenuSub("root");
                                    setMenuOpen((v) => !v);
                                }, title: "Click to start or complete this task", style: {
                                    cursor: "pointer",
                                    border: "none",
                                    font: "inherit",
                                }, children: [st.label, " \u25BE"] }), menuOpen && (_jsx("div", { className: "pm-table-free-menu", onClick: (e) => e.stopPropagation(), role: "menu", style: {
                                    position: "absolute",
                                    top: "calc(100% + 4px)",
                                    left: 0,
                                    zIndex: 200,
                                    background: "rgba(7, 29, 71, 0.98)",
                                    border: "1px solid rgba(255, 255, 255, 0.08)",
                                    borderRadius: 10,
                                    padding: 4,
                                    minWidth: 180,
                                    boxShadow: "0 12px 36px rgba(0, 0, 0, 0.4)",
                                    backdropFilter: "blur(12px)",
                                }, children: menuSub === "root" ? (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", role: "menuitem", className: "pm-table-free-menu-item", onClick: () => setMenuSub("start"), style: menuItemStyle, children: "\u25B6 Start task \u203A" }), onEdit && (_jsx("button", { type: "button", role: "menuitem", className: "pm-table-free-menu-item", onClick: () => {
                                                setMenuOpen(false);
                                                onEdit();
                                            }, style: menuItemStyle, children: "\u2713 Mark complete" }))] })) : (_jsxs(_Fragment, { children: [_jsxs("button", { type: "button", role: "menuitem", className: "pm-table-free-menu-item", onClick: () => void startNow(), style: menuItemStyle, children: ["\u25B6 Start now", _jsx("div", { style: {
                                                        fontSize: 11,
                                                        color: "var(--text-dim, #c5c9d3)",
                                                        marginTop: 2,
                                                        fontWeight: 400,
                                                    }, children: "Live Study Clock \u2014 counts up until Done" })] }), _jsxs("button", { type: "button", role: "menuitem", className: "pm-table-free-menu-item", onClick: scheduleLater, style: menuItemStyle, children: ["\uD83D\uDCC5 Schedule a time", _jsx("div", { style: {
                                                        fontSize: 11,
                                                        color: "var(--text-dim, #c5c9d3)",
                                                        marginTop: 2,
                                                        fontWeight: 400,
                                                    }, children: "Pick a slot on your calendar" })] }), _jsx("button", { type: "button", role: "menuitem", className: "pm-table-free-menu-item", onClick: () => setMenuSub("root"), style: { ...menuItemStyle, opacity: 0.7 }, children: "\u2190 Back" })] })) }))] })) : (_jsx("span", { className: `pill ${st.cls}`, children: st.label })), waiting && hasNudgeTarget(task.blocked_by_detail) && (_jsx("button", { type: "button", className: "pm-table-nudge", onClick: (e) => {
                            e.stopPropagation();
                            onNudge(task.blocked_by_detail);
                        }, title: "Nudge whoever's holding this up", children: "\uD83D\uDC4B Nudge" }))] }), _jsx("span", { className: "pm-table-cell-due", children: fmtDue(task.due_date) }), _jsxs("span", { className: "pm-table-cell-progress", children: [_jsx("span", { className: "pm-table-progress-bar", children: _jsx("span", { className: "pm-table-progress-fill", style: { width: `${task.progress_percent}%` } }) }), _jsxs("span", { className: "pm-table-progress-pct", children: [task.progress_percent, "%"] })] })] }));
}
function AddTaskInline({ project, onCancel, onSaved, }) {
    const { user: currentUser } = useAuth();
    // Start unassigned. Previously this defaulted to the creator's id,
    // which auto-assigned every inline-created task to whoever added it
    // — surprising behaviour when admins/managers add tasks for someone
    // else and never expected to be on it. Keeping currentUser around
    // for the dropdown "(me)" hint below, but the field is empty until
    // the user picks.
    void currentUser;
    const [title, setTitle] = useState("");
    const [assigneeId, setAssigneeId] = useState("");
    const [due, setDue] = useState("");
    const [predIds, setPredIds] = useState([]);
    const [coworkers, setCoworkers] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const list = await api.get("/users/coworkers");
                if (!cancelled)
                    setCoworkers(list);
            }
            catch {
                /* leave empty — dropdown becomes "(no coworkers loaded)" */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);
    function togglePred(id) {
        setPredIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    }
    async function save() {
        const t = title.trim();
        if (!t) {
            setError("Give the task a title.");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const body = {
                project_id: project.id,
                title: t,
                assignee_ids: assigneeId ? [assigneeId] : [],
                predecessor_task_ids: predIds,
                // Empty Depends-on here means "no dependency". Opt out of the
                // backend's auto-chain heuristic so the new task lands as an
                // independent node rather than getting silently wired onto the
                // previously rightmost task in the project. Confirmed
                // user-visible behaviour change on the table view.
                auto_chain: false,
            };
            if (due)
                body.due_date = due;
            await api.post("/tasks", body);
            await onSaved();
        }
        catch (e) {
            if (e instanceof ApiError)
                setError(e.message);
            else
                setError(e instanceof Error ? e.message : "Couldn't save");
        }
        finally {
            setSaving(false);
        }
    }
    return (_jsxs("div", { className: "pm-table-row pm-table-add-editor", children: [_jsxs("div", { className: "pm-table-add-editor-grid", children: [_jsx("input", { autoFocus: true, value: title, onChange: (e) => setTitle(e.target.value), placeholder: "Task title\u2026", onKeyDown: (e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                void save();
                            }
                            else if (e.key === "Escape") {
                                e.preventDefault();
                                onCancel();
                            }
                        } }), _jsxs("select", { value: assigneeId, onChange: (e) => setAssigneeId(e.target.value), title: "Assign to", children: [_jsx("option", { value: "", children: "Unassigned" }), coworkers.map((c) => (_jsx("option", { value: c.id, children: c.name }, c.id)))] }), _jsx("input", { type: "date", value: due, onChange: (e) => setDue(e.target.value), title: "Due date" })] }), _jsxs("div", { className: "pm-table-add-deps", children: [_jsx("span", { className: "muted small", children: "Depends on:" }), project.tasks.length === 0 ? (_jsx("span", { className: "muted small", children: "No other tasks yet." })) : (project.tasks.map((t) => (_jsxs("button", { type: "button", className: `pm-table-dep-chip ${predIds.includes(t.id) ? "active" : ""}`, onClick: () => togglePred(t.id), title: predIds.includes(t.id)
                            ? "Click to remove this predecessor"
                            : "Click to mark this task as a prerequisite", children: [predIds.includes(t.id) ? "✓ " : "+ ", t.title] }, t.id))))] }), error && _jsx("div", { className: "error pm-table-add-error", children: error }), _jsxs("div", { className: "pm-table-add-actions", children: [_jsx("button", { onClick: onCancel, disabled: saving, className: "btn-link", children: "Cancel" }), _jsx("button", { onClick: save, disabled: saving || !title.trim(), className: "btn-primary", children: saving ? "Saving…" : "Save" })] })] }));
}
// Edit-a-task modal — opened by double-clicking a row in the projects
// table (admin/manager only). Covers the common quick-edit fields the
// table surfaces: title, due date, estimate, status. Delete is the
// last action — a confirm() dialog gates the destructive call.
// Assignees + dependencies stay in the existing modals elsewhere.
function EditTaskModal({ task, onClose, onSaved, onDeleted, onRequestComplete, }) {
    const [title, setTitle] = useState(task.title);
    const [due, setDue] = useState(task.due_date ?? "");
    const [status, setStatus] = useState(task.status);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState(null);
    async function save() {
        if (!title.trim()) {
            setError("Give the task a title.");
            return;
        }
        // Marking complete is a documented event — hand off to the completion
        // flow rather than silently flipping status here. (Re-completing an
        // already-complete task, or any other status, saves inline.)
        if (status === "completed" && task.status !== "completed") {
            // Persist any title/due edits first so they aren't lost, then open
            // the completion modal which sets status=completed itself.
            try {
                if (title.trim() !== task.title || (due || null) !== (task.due_date ?? null)) {
                    setSaving(true);
                    await api.patch(`/tasks/${task.id}`, {
                        title: title.trim(),
                        due_date: due || null,
                    });
                }
            }
            catch (e) {
                setError(e instanceof Error ? e.message : "Couldn't save edits");
                setSaving(false);
                return;
            }
            setSaving(false);
            onRequestComplete();
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await api.patch(`/tasks/${task.id}`, {
                title: title.trim(),
                status,
                due_date: due || null,
            });
            await onSaved();
        }
        catch (e) {
            if (e instanceof ApiError)
                setError(e.message);
            else
                setError(e instanceof Error ? e.message : "Couldn't save");
        }
        finally {
            setSaving(false);
        }
    }
    async function remove() {
        if (!confirm(`Delete "${task.title}"? This removes the task plus every block, checklist item, and kill-list row linked to it across the team. Cannot be undone.`)) {
            return;
        }
        setDeleting(true);
        setError(null);
        try {
            await api.del(`/tasks/${task.id}`);
            await onDeleted();
        }
        catch (e) {
            if (e instanceof ApiError)
                setError(e.message);
            else
                setError(e instanceof Error ? e.message : "Couldn't delete");
            setDeleting(false);
        }
    }
    return (_jsx("div", { className: "modal-backdrop", onClick: onClose, children: _jsxs("div", { className: "modal", onClick: (e) => e.stopPropagation(), children: [_jsx("h2", { children: "Edit task" }), _jsx("p", { className: "muted small", style: { marginTop: "-0.5rem" }, children: "Quick edit: rename, retime, change status, or delete entirely." }), _jsxs("label", { children: ["Title", _jsx("input", { value: title, onChange: (e) => setTitle(e.target.value), autoFocus: true })] }), _jsx("div", { className: "row", children: _jsxs("label", { children: ["Due date ", _jsx("span", { className: "muted small", children: "\u00B7 optional" }), _jsx("input", { type: "date", value: due, onChange: (e) => setDue(e.target.value) })] }) }), _jsxs("label", { children: ["Status", _jsxs("select", { value: status, onChange: (e) => setStatus(e.target.value), children: [_jsx("option", { value: "not_started", children: "Not started" }), _jsx("option", { value: "in_progress", children: "In progress" }), _jsx("option", { value: "blocked", children: "Blocked" }), _jsx("option", { value: "completed", children: "Completed" })] })] }), error && _jsx("div", { className: "error", children: error }), _jsxs("div", { className: "modal-actions", children: [_jsx("button", { type: "button", className: "btn-danger", onClick: remove, disabled: saving || deleting, style: { marginRight: "auto" }, title: "Delete this task", children: deleting ? "Deleting…" : "Delete task" }), _jsx("button", { type: "button", onClick: onClose, disabled: saving || deleting, children: "Cancel" }), _jsx("button", { type: "button", className: "btn-primary", onClick: save, disabled: saving || deleting || !title.trim(), children: saving ? "Saving…" : "Save" })] })] }) }));
}
